import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Box, Button, FormControlLabel, Grid, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const LEVEL = { enterprise: 'enterprise', organization: 'organisation', region: 'region', clinic: 'office' };
const EMPTY = {
  practice_name: '', address: '', phone: '', email: '', website: '',
  registration: '', header_note: '', footer_text: '', show_logo: true,
};

/**
 * The header and footer printed on a patient's visit sheet, for the level this admin
 * runs. An office uses its own; without one it uses the nearest above it (region →
 * organisation → enterprise); without any, its stored name and address. The sheet
 * always ends "Powered by ScribeLive" — shown in the preview, not editable.
 */
export default function LetterheadSettings() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const apply = (data) => {
    setInfo(data);
    const own = data?.own;
    setForm({ ...EMPTY, ...(own?.letterhead || own?.defaults || {}) });
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/v1/branding/letterhead`).then(({ data }) => apply(data)).catch(() => {});
  }, []);

  if (!info?.own) return null;
  const own = info.own;
  const level = LEVEL[own.scope] || 'account';
  const inherited = !own.letterhead && info.effective?.source_scope && info.effective.source_scope !== own.scope
    ? info.effective : null;

  const set = (k) => (e) => setForm({ ...form, [k]: k === 'show_logo' ? e.target.checked : e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/v1/branding/letterhead`, form);
      apply(data);
      toast.success('Letterhead saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the letterhead');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API_URL}/api/v1/branding/letterhead`);
      apply(data);
      toast.success('Using the letterhead above again');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove the letterhead');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(4, 47, 46, 0.12)', mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Print letterhead</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The header and footer on the visit sheet a provider prints for the patient
        {own.scope !== 'clinic' ? ` — used by every office in this ${level} that has no letterhead of its own` : ''}.
      </Typography>
      {inherited && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Printouts here currently use <b>{inherited.source_name}</b>’s letterhead. Save your own to override it.
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12}><TextField fullWidth size="small" label="Practice / hospital name" value={form.practice_name} onChange={set('practice_name')} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" multiline minRows={2} label="Address" value={form.address} onChange={set('address')} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Phone" value={form.phone} onChange={set('phone')} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Email" value={form.email} onChange={set('email')} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Website" value={form.website} onChange={set('website')} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" label="Registration / licence no." value={form.registration} onChange={set('registration')} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" label="Header note (e.g. opening hours)" value={form.header_note} onChange={set('header_note')} /></Grid>
        <Grid item xs={12}><TextField fullWidth size="small" multiline minRows={2} label="Footer (e.g. emergency number)" value={form.footer_text} onChange={set('footer_text')} /></Grid>
        <Grid item xs={12}>
          <FormControlLabel control={<Switch checked={form.show_logo} onChange={set('show_logo')} />} label="Show our logo on printouts" />
        </Grid>
      </Grid>

      {/* A miniature of the printed sheet's top and bottom. */}
      <Box sx={{ mt: 2, border: '1px dashed rgba(4,47,46,0.25)', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
        <Typography variant="caption" color="text.secondary">Preview</Typography>
        <Box sx={{ borderBottom: '2px solid #0f766e', pb: 1, mb: 1 }}>
          <Typography fontWeight={800}>{form.practice_name || '—'}</Typography>
          {form.address && <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-line' }}>{form.address}</Typography>}
          <Typography variant="caption" sx={{ display: 'block' }}>
            {[form.phone, form.email, form.website].filter(Boolean).join('  ·  ')}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>…visit details…</Typography>
        <Box sx={{ borderTop: '1px solid #d1d5db', mt: 1, pt: 0.75, textAlign: 'center' }}>
          {form.footer_text && <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-line' }}>{form.footer_text}</Typography>}
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>Powered by ScribeLive</Typography>
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={save} disabled={busy}>Save letterhead</Button>
        {own.letterhead && (
          <Button color="inherit" onClick={reset} disabled={busy}>
            {own.scope === 'clinic' || own.scope === 'region' ? 'Use the one above instead' : 'Remove'}
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        “Powered by ScribeLive” is always printed at the bottom.
      </Typography>
    </Paper>
  );
}
